import { Link } from '@tanstack/react-router';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  baseUrl: string;
}

export function Pagination({ currentPage, totalPages, baseUrl }: PaginationProps) {
  const prevUrl = currentPage > 1
    ? currentPage === 2
      ? baseUrl
      : `${baseUrl}/page/${currentPage - 1}`
    : null;

  const nextUrl = currentPage < totalPages
    ? `${baseUrl}/page/${currentPage + 1}`
    : null;

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | '...')[] = [];
    const showPages = 5;

    if (totalPages <= showPages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  if (totalPages <= 1) return null;

  return (
    <nav className="flex items-center justify-center gap-2 mt-12" aria-label="Paginação do blog">
      {/* Previous */}
      {prevUrl ? (
        <Link
          to={prevUrl}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 text-navy-700 hover:border-gold-300 hover:text-gold-600 transition-colors"
          aria-label="Página anterior"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline">Anterior</span>
        </Link>
      ) : (
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-100 text-neutral-300 cursor-not-allowed">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline">Anterior</span>
        </span>
      )}

      {/* Page Numbers */}
      <div className="flex items-center gap-1">
        {pageNumbers.map((page, index) => (
          page === '...' ? (
            <span key={`ellipsis-${index}`} className="px-3 py-2 text-neutral-400">...</span>
          ) : page === currentPage ? (
            <span
              key={page}
              className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gold-500 text-white font-semibold"
              aria-current="page"
            >
              {page}
            </span>
          ) : (
            <Link
              key={page}
              to={page === 1 ? baseUrl : `${baseUrl}/page/${page}`}
              className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-neutral-200 text-navy-700 hover:border-gold-300 hover:text-gold-600 transition-colors"
            >
              {page}
            </Link>
          )
        ))}
      </div>

      {/* Next */}
      {nextUrl ? (
        <Link
          to={nextUrl}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 text-navy-700 hover:border-gold-300 hover:text-gold-600 transition-colors"
          aria-label="Próxima página"
        >
          <span className="hidden sm:inline">Próxima</span>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      ) : (
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-100 text-neutral-300 cursor-not-allowed">
          <span className="hidden sm:inline">Próxima</span>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </span>
      )}
    </nav>
  );
}
